import { useMemo } from "react";
import {
  resolveLocationsToStoreStatuses,
  resolveStoreStatusToLocations,
  type StoreStatusOption
} from "./useFetchStoreStatusOptions";

/**
 * Bidirectional cross-filtering between store status and sales_location.
 *
 * - Store status → sales_location: resolved locations merged into filtered context options
 * - Sales_location → store status: relevant statuses returned for the StoreStatusFilter
 *
 * Shared between the forecast and summary pages.
 */
export function useStoreStatusContextMerge(
  selectedStoreStatuses: string[],
  selectedSalesLocations: string[] | undefined,
  storeStatusOptions: StoreStatusOption[] | undefined,
  filteredContextOptions: Record<string, string[]> | undefined
) {
  // Forward: store status → sales locations
  const resolvedStoreLocations = useMemo(
    () =>
      selectedStoreStatuses.length > 0 && storeStatusOptions
        ? resolveStoreStatusToLocations(
            selectedStoreStatuses,
            storeStatusOptions
          )
        : [],
    [selectedStoreStatuses, storeStatusOptions]
  );

  // Reverse: sales locations → relevant store statuses
  const relevantStoreStatuses = useMemo(
    () =>
      selectedSalesLocations &&
      selectedSalesLocations.length > 0 &&
      storeStatusOptions
        ? resolveLocationsToStoreStatuses(
            selectedSalesLocations,
            storeStatusOptions
          )
        : [],
    [selectedSalesLocations, storeStatusOptions]
  );

  // Merge resolved locations into filtered context options
  const mergedFilteredContextOptions = useMemo(() => {
    if (resolvedStoreLocations.length === 0) return filteredContextOptions;
    const base = filteredContextOptions ?? {};
    const existing = base.sales_location ?? [];
    const merged =
      existing.length > 0
        ? existing.filter((loc: string) => resolvedStoreLocations.includes(loc))
        : resolvedStoreLocations;
    return { ...base, sales_location: merged };
  }, [filteredContextOptions, resolvedStoreLocations]);

  return {
    resolvedStoreLocations,
    mergedFilteredContextOptions,
    relevantStoreStatuses
  };
}
